import type { IPriceHistoryEntry } from "store/priceHistory";

/**
 * Reduce a date to its UTC calendar day (YYYY-MM-DD). Bounds on a price-history entry
 * are inclusive at this granularity — a sale anywhere on the start or end day is covered.
 * Mirrors the backend helper in packages/crealobackend/src/utils/priceHistory.ts.
 */
export const toDayKey = (d: Date | string): string =>
    typeof d === "string" ? d.slice(0, 10) : toUtcMidnightISO(d).slice(0, 10);

/**
 * Build an ISO string anchored at UTC midnight of the date's *local* calendar day.
 * The native date picker returns a Date representing midnight in the user's timezone, so
 * `.toISOString()` would shift the day for users west of UTC (e.g. CEST June 15 → June 14
 * in UTC). This normalization preserves the day the user actually picked when the value
 * is later compared against the backend's UTC day-key.
 */
export const toUtcMidnightISO = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

export const previousDay = (date: Date): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d;
};

/** Shift a UTC-midnight ISO bound by n days, staying in UTC (local-time helpers would drift west of UTC). */
export const shiftUtcDayISO = (iso: string, days: number): string => {
    const d = new Date(iso);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days)).toISOString();
};

/**
 * Periods of a (channelId, currency) pair form a contiguous chain: each period starts
 * the day after the previous one ends. These find an entry's direct neighbours in that chain.
 */
export const findChainPredecessor = (
    entries: IPriceHistoryEntry[],
    entry: Pick<IPriceHistoryEntry, "id" | "channelId" | "currency" | "startDate">,
): IPriceHistoryEntry | undefined =>
    entry.startDate
        ? entries.find(
              (e) =>
                  e.id !== entry.id &&
                  e.channelId === entry.channelId &&
                  e.currency === entry.currency &&
                  e.endDate !== null &&
                  toDayKey(e.endDate) === toDayKey(shiftUtcDayISO(entry.startDate!, -1)),
          )
        : undefined;

export const findChainSuccessor = (
    entries: IPriceHistoryEntry[],
    entry: Pick<IPriceHistoryEntry, "id" | "channelId" | "currency" | "endDate">,
): IPriceHistoryEntry | undefined =>
    entry.endDate
        ? entries.find(
              (e) =>
                  e.id !== entry.id &&
                  e.channelId === entry.channelId &&
                  e.currency === entry.currency &&
                  e.startDate !== null &&
                  toDayKey(e.startDate) === toDayKey(shiftUtcDayISO(entry.endDate!, 1)),
          )
        : undefined;

export const sortPriceHistoryEntries = (a: IPriceHistoryEntry, b: IPriceHistoryEntry): number => {
    if (a.startDate === null) return 1;
    if (b.startDate === null) return -1;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
};

/**
 * Chronological order for display: oldest first, newest last. An undated entry
 * (null startDate) covers all time and sorts to the very top. Keeps the grid
 * order stable — a newly added period always appears at the bottom, both while
 * editing and after saving.
 */
export const sortPriceHistoryEntriesAsc = (a: IPriceHistoryEntry, b: IPriceHistoryEntry): number =>
    -sortPriceHistoryEntries(a, b);

/**
 * The last dated period of a (channelId, currency) chain must stay open-ended:
 * its end date cannot be set, so a price always covers the upcoming period.
 * The undated default (null start AND end) is not part of the dated chain.
 */
export const isLastDatedPriceInChain = (
    entries: IPriceHistoryEntry[],
    entry: Pick<IPriceHistoryEntry, "id" | "channelId" | "currency" | "startDate" | "endDate">,
): boolean => {
    if (entry.startDate === null && entry.endDate === null) return false;

    const sameChain = entries.filter(
        (e) =>
            e.channelId === entry.channelId &&
            e.currency === entry.currency &&
            (e.startDate !== null || e.endDate !== null),
    );
    if (sameChain.length === 0) return false;

    const last = [...sameChain].sort(sortPriceHistoryEntriesAsc).at(-1);
    return last?.id === entry.id;
};

/** Open-end the last dated price of each (channelId, currency) chain. */
export const openLastDatedPrices = (entries: IPriceHistoryEntry[]): IPriceHistoryEntry[] =>
    entries.map((entry) =>
        isLastDatedPriceInChain(entries, entry) && entry.endDate !== null ? { ...entry, endDate: null } : entry,
    );

/**
 * An entry is "past" when its endDate's day is strictly before the period-start day —
 * used to hide stale rows behind the "show history" toggle in readonly mode.
 */
export const isPriceHistoryEntryPast = (entry: IPriceHistoryEntry, periodStart?: Date): boolean =>
    entry.endDate !== null && !!periodStart && toDayKey(entry.endDate) < toDayKey(periodStart);

/**
 * A price history entry is connected to the active accounting period when its date range
 * overlaps the active period. The default placeholder entry (null startDate AND endDate)
 * covers all time and is always connected.
 */
export const isPriceHistoryEntryConnectedToActivePeriod = (
    entry: IPriceHistoryEntry,
    activePeriod: { periodStart: string | Date | undefined; periodEnd: string | Date | undefined } | undefined,
): boolean => {
    if (!activePeriod || !activePeriod.periodStart || !activePeriod.periodEnd) return false;
    return hasDateOverlap(
        entry.startDate ? new Date(entry.startDate) : null,
        entry.endDate ? new Date(entry.endDate) : null,
        new Date(activePeriod.periodStart),
        new Date(activePeriod.periodEnd),
    );
};

/**
 * Check if two date ranges overlap. Bounds are inclusive at calendar-day granularity, so
 * two ranges that share a single day (e.g. one ends Jun 15, the other starts Jun 15) overlap.
 * A null start means -infinity; a null end means +infinity.
 */
export const hasDateOverlap = (
    startA: Date | null,
    endA: Date | null,
    startB: Date | null,
    endB: Date | null,
): boolean => {
    const sA = startA ? toDayKey(startA) : "0000-00-00";
    const sB = startB ? toDayKey(startB) : "0000-00-00";
    const eA = endA ? toDayKey(endA) : "9999-12-31";
    const eB = endB ? toDayKey(endB) : "9999-12-31";

    return sA <= eB && sB <= eA;
};

/**
 * Find which of the new entry's bounds (startDate / endDate) fall inside an existing
 * entry's range, for the same (channelId, currency) combination. Returns null when
 * there is no overlap.
 *
 * - If exactly one bound lies inside an existing range, only that field is flagged.
 * - If neither bound lies inside (i.e. the new range wraps an existing one), both are
 *   flagged since either could be moved to resolve the conflict.
 */
export const findOverlapFields = (
    entries: IPriceHistoryEntry[],
    newEntry: {
        channelId?: number;
        currency: string;
        startDate: Date | null;
        endDate: Date | null;
        countryMarket?: string;
    },
    excludeEntryId?: number,
): { startOverlaps: boolean; endOverlaps: boolean } | null => {
    const matchingEntries = entries.filter(
        (entry) =>
            entry.channelId === newEntry.channelId &&
            entry.currency === newEntry.currency &&
            entry.id !== excludeEntryId,
    );

    for (const entry of matchingEntries) {
        const existStart = entry.startDate ? new Date(`${entry.startDate.slice(0, 10)}T00:00:00`) : null;
        const existEnd = entry.endDate ? new Date(`${entry.endDate.slice(0, 10)}T00:00:00`) : null;
        if (!hasDateOverlap(existStart, existEnd, newEntry.startDate, newEntry.endDate)) continue;

        const existStartKey = existStart ? toDayKey(existStart) : "0000-00-00";
        const existEndKey = existEnd ? toDayKey(existEnd) : "9999-12-31";
        const newStartKey = newEntry.startDate ? toDayKey(newEntry.startDate) : "0000-00-00";
        const newEndKey = newEntry.endDate ? toDayKey(newEntry.endDate) : "9999-12-31";

        const startOverlaps = newStartKey >= existStartKey && newStartKey <= existEndKey;
        const endOverlaps = newEndKey >= existStartKey && newEndKey <= existEndKey;

        if (!startOverlaps && !endOverlaps) return { startOverlaps: true, endOverlaps: true };
        return { startOverlaps, endOverlaps };
    }
    return null;
};

/**
 * Validate that only one open-ended period exists per (channelId, currency, countryMarket) combination.
 * Returns true if there is already an open-ended entry
 */
export const validateSingleOpenEnded = (
    entries: IPriceHistoryEntry[],
    newEntry: {
        channelId?: number;
        currency: string;
        endDate: Date | null;
        countryMarket?: string;
    },
    excludeEntryId?: number,
): boolean => {
    if (newEntry.endDate !== null) return false;

    return entries.some(
        (entry) =>
            entry.channelId === newEntry.channelId &&
            entry.currency === newEntry.currency &&
            entry.endDate === null &&
            entry.id !== excludeEntryId,
    );
};

const addOneDay = (dayKey: string): string => {
    const d = new Date(dayKey);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

/**
 * Validate that there are no gaps between consecutive price history periods for a given channel.
 * A gap exists when the next period's startDate day is strictly after the day right after the
 * current period's endDate day — i.e. at least one full calendar day is missing between them.
 * - A default entry (startDate null AND endDate null) covers all time → no gap possible.
 * - An open-ended entry (endDate null) covers all time after its start → no gap after it.
 * Returns true if a gap is found.
 */
export const validateNoGap = (entries: IPriceHistoryEntry[], channelId?: number): boolean => {
    const channelEntries = entries.filter((e) => e.channelId === channelId);

    const currencies = [...new Set(channelEntries.map((e) => e.currency))];

    const hasGapInSortedEntries = (sorted: IPriceHistoryEntry[]): boolean => {
        // A default entry (null startDate AND null endDate) covers all time — no gaps possible
        if (sorted.some((e) => e.startDate === null && e.endDate === null)) return false;

        for (let i = 0; i < sorted.length - 1; i++) {
            const current = sorted[i];
            const next = sorted[i + 1];

            // Open-ended current entry covers all time after it — no gap possible
            if (current.endDate === null) continue;

            const currentEndNextDay = addOneDay(toDayKey(current.endDate));
            // null-startDate entries sort first, so next.startDate is always non-null here
            const nextStartDay = toDayKey(next.startDate!);

            if (new Date(nextStartDay) > new Date(currentEndNextDay)) return true;
        }

        return false;
    };

    return currencies.some((currency) => {
        const currencyEntries = channelEntries
            .filter((e) => e.currency === currency)
            .sort(sortPriceHistoryEntries)
            .reverse();
        return hasGapInSortedEntries(currencyEntries);
    });
};
